<!-- src/pages/saju-input/index.vue -->
<script setup>
// vue
import { Transition } from 'vue';
import { useRoute } from 'vue-router';
import { cloneDeep } from 'lodash';
import axios from 'axios';
import { canonicalUrl, applySeoMeta } from '@/utils/seo';

// config & store
import { DEV_MODE } from '@/config/dev-mode';
import { useMyInfoStore } from '@/store/modules/myinfo';
import { useManseStore } from '@/store/modules/manseStore';

// composables
import fiveMixin from '@/composables/fiveMixin';
import useFunctions from '@/composables/useFunctions';
import { getUserLogin } from '@/composables/useAuthStore';
import { usePopups } from '@/composables/saju/usePopups';
import { useClientType } from '@/composables/useClientType';
import { useRnBridge } from '@/composables/useRnBridge';
import { useBackLayer } from '@/composables/useAppBack';

import { useWoonManagement } from '@/composables/saju/useWoonManagement.js';
import { useSajuSave } from '@/composables/saju/useSajuSave.js';
import {
  groupListOpen,
  isPopSajuGroupChange,
  closeGroupChange,
  openGroupSetting,
  isPopSajuGroupAdd,
  isPopSajuGroupSetting,
  currentTab,
  closeGroupAdd,
  closeGroupSetting,
  isPopMemoList,
  isPopMemoReg,
  closeMemoList,
  closeMemoReg,
} from '@/composables/useGroup';

// api
import { fastSearch, userGroupDel, logGuestUserHistory } from '@/api/Eoway';
import { getDetail, getMemoList, updateGuestUserName } from '@/api/UserGroup';
import { addContentLogAPI } from '@/api/logServiceApi';
import { contentOptionMap } from '@/components/saju/data/manseCardLogData';

// components
import SajuForm from '@/components/saju/SajuForm.vue';
import SajuOption from '@/components/saju/SajuOption.vue';
import SajuHeader from '@/components/saju/SajuHeader.vue';
import SajuResultHeader from '@/components/saju/SajuResultHeader.vue';
import SajuResultPlaceholder from '@/components/saju/SajuResultPlaceholder.vue';
import SajuMyeongsik from '@/components/saju/SajuMyeongsik.vue';
import SajuResultPro from '@/components/saju/SajuResultPro.vue';
import SajuLeftBanner from '@/components/saju/SajuLeftBanner.vue';
import WoonContainer from '@/components/saju/WoonContainer.vue';
import SinsalCardGrid from '@/components/saju/SinsalCardGrid.vue';
import OhaengChart from '@/components/saju/OhaengChart.vue';
import OhaengSummary from '@/components/saju/OhaengSummary.vue';
import ComLatest from '@/components/saju/ComLatest.vue';
import LoadingSpinner from '@/components/saju/LoadingSpinner.vue';
import QuickMenu from '@/components/saju/QuickMenu.vue';
import MobileQuickMenu from '@/components/saju/MobileQuickMenu.vue';
import MobileOptionDrawer from '@/components/saju/MobileOptionDrawer.vue';
import MobileHeader from '@/components/common/MobileHeader.vue';
import Snackbar from '@/components/common/Snackbar.vue';
import AlertComponent from '@/components/common/Alert.vue';
import ConfirmComponent from '@/components/common/Confirm.vue';

// popup
import PopSajuGroupChange from '@/components/saju-service/PopSajuGroupChange.vue';
import PopAddGroup from '@/components/saju-service/PopAddGroup.vue';
import PopMemo from '@/components/saju-service/PopMemo.vue';
import PopGanji from '@/components/saju/PopGanji.vue';
import PopArea from '@/components/saju/PopArea.vue';
import PopMansePrint from '@/components/saju/PopMansePrint.vue';
import PopNameComponent from '@/components/saju/PopName.vue';
import PopCardDetail from '@/components/saju/PopCardDetail.vue';
import PopIlgan from '@/components/saju/PopIlgan.vue';
import PopFiveStar from '@/components/saju/PopFiveStar.vue';
import PopKSTOption from '@/components/saju/PopKSTOption.vue';
import PopJasiOption from '@/components/saju/PopJasiOption.vue';
import PopSolarTermOption from '@/components/saju/PopSolarTermOption.vue';
import PopQrBox from '@/components/saju/PopQrBox.vue';
import PopShareBox from '@/components/saju/PopShareBox.vue';
import PopSummerTime from '@/components/documents/PopSummerTime.vue';

const systemId = import.meta.env.VITE_APP_SYSTEMID;

definePage({ meta: { layout: 'manse', title: '사주입력' } });

// =============== 1. 초기 설정 ===============
// 팝업 관련 변수,함수
const {
  // 상태 (State)
  isPopGanjiBox,
  isPopAreaBox,
  isPopQrBox,
  isOpenPopKSTOption,
  isOpenPopJasiOption,
  isOpenPopSTOption,
  isPopSummerTime,
  isPopManseDetail,

  // 함수 (Actions)
  openPopGanjiBox,
  handleFocusArea,
  qrOpen,
  closePopQrBox,
  closePopQuickMenu,
  clickTooltip,
  openPopOption,
  closePopOption,
  openSummerTime,
  closePopManseDetail,
} = usePopups();

const route = useRoute();
const router = useRouter();

applySeoMeta({
  title: '무료사주팔자, 사주조회, 사주간지, 야자시',
  description:
    '로그인 없이 무료로 사주팔자 확인. 대운·세운·월운, 오행, 합형충파해까지 전문 해석 제공. 야자시·한국시·입춘 기준과 해외 출생지 보정 지원.',
  url: canonicalUrl(route.fullPath),
});

const { ...five } = fiveMixin();
const { ...useFun } = useFunctions();

// 대운/세운/월운 관리 컴포저블
const woonManagement = useWoonManagement();

// 사주 저장 관리 컴포저블
const sajuSave = useSajuSave();

const { isMobileUi: isMobile, isApp } = useClientType();
const { sendMessageToApp } = useRnBridge();


const isLoading = ref(false);

const { selectedOptions, setUserSelectOption, checkUserSelectOptions, fetchMyInfo } =
  useMyInfoStore();
const manseStore = useManseStore();

const colorMode = ref('fntcolor01');

const rsDataNor = ref(false);

const isOptionInput = ref(true),
  rsData01 = ref(false),
  isCheckboxChecked = ref(false);

const isOptionDrawerOpen = ref(false);

// =============== 2. 입력 폼 ===============
const DEFAULT_FORM_OPTIONS = Object.freeze({
  option1: true,
  option2: false,
  option3: false,
  option4: true,
});

const DEFAULT_CALENDAR_OPTIONS = Object.freeze({
  isLunar: 'false',
  isLeapYear: null,
});

const createEmptyFormData = () => ({
  year: '',
  month: '',
  day: '',
  hour: '',
  min: '',
  gender: null,
  userName: '',
  isLunar: DEFAULT_CALENDAR_OPTIONS.isLunar,
  lunar: null,
  isLeapYear: DEFAULT_CALENDAR_OPTIONS.isLeapYear,
  bornAreaId: null,
  birth_time: null,
  birth: null,
  birthTime: null,
  unknownTime: null,
  option1: DEFAULT_FORM_OPTIONS.option1,
  option2: DEFAULT_FORM_OPTIONS.option2,
  option3: DEFAULT_FORM_OPTIONS.option3,
  option4: DEFAULT_FORM_OPTIONS.option4,
  addressTxt: null,
  lang: 'KOR',
  langYN: 'N',
  userGroupId: null,
  shortly: false,
  blCheck: 'N',
});

const formData = ref(createEmptyFormData());

/** '조회하기'로 받은 사주 데이터 */
const sajuData = ref({});
/** '조회하기'에 입력했던 formData 복사 */
const sajuDataForm = ref({});

const rsDataAll = ref({});

// =============== 3. 결과 데이터 ===============
const shareResultTextByNative = async () => {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') return false;

  const shareData = {
    title: '낭만만세력 사주결과',
    text: useFun.convertShareTxt(rsDataAll.value),
  };

  if (typeof navigator.canShare === 'function' && !navigator.canShare(shareData)) {
    return false;
  }

  await navigator.share(shareData);
  return true;
};

const isShareCanceledError = (error) => {
  const errorName = error?.name || '';
  const errorMessage = error?.message || '';
  return (
    errorName === 'AbortError' ||
    /abort|cancel|dismiss/i.test(errorName) ||
    /abort|cancel|dismiss/i.test(errorMessage)
  );
};

// 모바일 공유하기
const isMobileShareOpen = ref(false);
const openMobileShare = async () => {
  addContentLogAPI('manse_btn_share');

  if (Object.keys(rsDataAll.value || {}).length < 1) {
    showDialogAlert('사주조회 후 이용하여 주시기 바랍니다.');
    return;
  }

  if (isMobile.value) {
    try {
      const isShared = await shareResultTextByNative();
      if (isShared) return;
    } catch (error) {
      if (isShareCanceledError(error)) return;
      DEV_MODE && console.error('기기 기본 공유 실패:', error);
    }
  }

  isMobileShareOpen.value = true;
};
const closeMobileShare = () => {
  isMobileShareOpen.value = false;
};

const langMode = ref('CHNKOR'); // 'CHNKOR' | 'CHN' | 'KOR'
const sortMode = ref('desc');

const transdata = ref({});
const inputdata = ref({});
const birthData = ref({});
// 백엔드에서 제공하는 정확한 양력/음력 날짜 (transdata.solar/lunar 대체)
const solarDate = ref(null);
const solarWeekday = ref(null);
const lunarDate = ref(null);
const birthJijanggan = ref({});
const birthTitle = ref('시일월년');
const gongDataVal = ref(null);
const birthFiveCnt = ref({});

const hapDataValEarth = ref(null);
const hapDataVal = ref(null);

const isSaved = ref(false);
const isShowBanner = ref(true);

const birthEightTxt = ref('');
const sajuCreatedDate = ref(null);

// =============== 4. 운세 (대운/세운/월운) ===============
// 대운/세운/월운 관련 변수들은 useWoonManagement에서 가져옴
const {
  curDaeun,
  daeunsData,
  selectDaeun,
  yearsData,
  monthsData,
  isDaeunCheck,
  isDaeunYear,
  isDaeunMonth,
  daeunCurNum,
  daeunYearCurNum,
  daeunMonthCurNum,
  seunIndex,
  selectedBlylml,
  prevBlylml,
  hapBlDataSky,
  hapBlDataEarth,
  isSeWoonOpen,
  isWolWoonOpen,
  // 메서드
  getWoon,
  getSeun,
  getMonth,
  clickDaeunCheck,
  applyWoon,
  reverseDaeunData,
  toggleSeWoon,
  toggleWolWoon,
} = woonManagement;

// useWoonManagement 초기화 (의존성 주입은 searchEoway 후에 수행)
/** 대운적용 기능 사용여부*/
const isapplyWoon = ref(true);
// const isapplyWoon = ref(DEV_MODE);

/** 현재세운 데이터 (모바일에서 사용) */
const curSeun = computed(() => {
  if (!yearsData.value?.sky?.length) return { sky: [], earth: [], year: null };
  // 현재 선택된 세운 번호(연도)에 해당하는 데이터 찾기
  const skyData = yearsData.value.sky.find((item) => item.num === daeunYearCurNum.value);
  const earthData = yearsData.value.earth.find((item) => item.num === daeunYearCurNum.value);
  return {
    sky: skyData ? [skyData] : [],
    earth: earthData ? [earthData] : [],
    year: daeunYearCurNum.value, // 현재 선택된 연도
  };
});

/** 첫 대운 변기일 정보 */
const bigluckinfo = computed(() => {
  return sajuData.value?.bigluckinfo || null;
});

/** 첫 대운 변기일 포맷팅 (YYYY-MM-DD 형식으로) */
const firstDaeunDateFormatted = computed(() => {
  if (!bigluckinfo.value?.first_daeun_date) return null;
  // "1912-03-21 11:42:15" -> "1912-03-21"
  return bigluckinfo.value.first_daeun_date.split(' ')[0];
});

// 모바일 접기 버튼: 대운/세운 → 세운+월운 토글, 월운 → 월운만 토글
const isMobileSeWoonOpen = ref(true);
const isMobileWolWoonOpen = ref(true);
const isMobileAllSubOpen = computed(() => isMobileSeWoonOpen.value && isMobileWolWoonOpen.value);
const toggleMobileDaeun = () => {
  if (isMobileAllSubOpen.value) {
    isMobileSeWoonOpen.value = false;
    isMobileWolWoonOpen.value = false;
  } else {
    isMobileSeWoonOpen.value = true;
    isMobileWolWoonOpen.value = true;
  }
};
const toggleMobileSeWoon = () => {
  isMobileSeWoonOpen.value = !isMobileSeWoonOpen.value;
  if (!isMobileSeWoonOpen.value) isMobileWolWoonOpen.value = false;
  else isMobileWolWoonOpen.value = true;
};
const toggleMobileWolWoon = () => {
  isMobileWolWoonOpen.value = !isMobileWolWoonOpen.value;
};

// =============== 5. 옵션/설정 ===============
/** 사주조회 Params 저장 */
const searchParams = ref(null);

const selectedGroup = ref({});
const selectedListItem = ref({});
const userGroupId = ref(null);
const userGroupName = ref('등록 사주');
const isUnknownTime = ref(false);
const isFutureSaju = ref(false);

const currentCardIndex = ref(null);

const snackbar = ref(null);

//TODO: 전문가용
const toggleProMode = ref('nor'); // 프로: 'pro', 일반: 'nor'
const dataOrder = ref(['hour', 'day', 'month', 'year']);
const firstSegment = ref({});
const secondSegment = ref({});
const thirdSegment = ref({});
const fourthSegment = ref({});

const changeProMode = async (mode) => {
  addContentLogAPI({
    contentType: 'manse_option_pro_gen_opt3',
    option3: mode === 'pro' ? '프로' : '일반',
  });
  toggleProMode.value = mode;
  if (mode === 'pro') await searchEowayTrans('CHNKOR');
  sessionStorage.setItem('toggleProMode', toggleProMode.value);
  // 모드 바뀔 때 일간 팝업 닫기
  if (mode === 'nor') await resetSkyDay();
  isPopIlgan.value = false;
};

const isNormalModeFirst = ref(true);

watch(toggleProMode, (newMode) => {
  if (newMode === 'nor' && isNormalModeFirst.value) {
    isNormalModeFirst.value = false;
    addContentLogAPI({
      contentType: 'manse_option_sort_opt3',
      option3: '시일월년',
    });
    addContentLogAPI({
      contentType: 'manse_option_chn_kor_opt3',
      option3: '전체',
    });
    addContentLogAPI({
      contentType: 'manse_option_color_opt3',
      option3: '바탕색',
    });
  }
});

/** 한글과 한자 혼용 */
// 한자/한글 버전일 경우 프린트는 일부 한자 일부 한글
const showHanja = (value, position) => {
  return langMode.value !== 'KOR' ? five.getHanjaTxt(value, position) : value;
};

async function setData() {
  if (rsDataAll.value) {
    const totalLength = rsDataAll.value.hundredsyears.reduce(
      (acc, item) => acc + (item.value ? item.value.length : 0),
      0,
    );

    const segmentLength = 25;
    firstSegment.value = rsDataAll.value?.hundredsyears.reduce((acc, item) => {
      const filteredValue = item.value.filter((v) => v.age <= segmentLength);
      if (filteredValue.length > 0) {
        acc.push({ ...item, value: filteredValue });
      }
      return acc;
    }, []);
    secondSegment.value = rsDataAll.value?.hundredsyears.reduce((acc, item) => {
      const filteredValue = item.value.filter(
        (v) => v.age > segmentLength && v.age <= segmentLength * 2,
      );
      if (filteredValue.length > 0) {
        acc.push({ ...item, value: filteredValue });
      }
      return acc;
    }, []);

    thirdSegment.value = rsDataAll.value?.hundredsyears.reduce((acc, item) => {
      const filteredValue = item.value.filter(
        (v) => v.age > segmentLength * 2 && v.age <= segmentLength * 3,
      );
      if (filteredValue.length > 0) {
        acc.push({ ...item, value: filteredValue });
      }
      return acc;
    }, []);

    fourthSegment.value = rsDataAll.value?.hundredsyears.reduce((acc, item) => {
      const filteredValue = item.value.filter((v) => v.age > segmentLength * 3 && v.age <= 100);
      if (filteredValue.length > 0) {
        acc.push({ ...item, value: filteredValue });
      }
      return acc;
    }, []);
  }
}

// =============== 7. 저장/삭제/공유 ===============

// =============== 6. 조회/검색 ===============
const searchEowayDo = async (callback = () => {}) => {
  resetSajuData();
  isSaved.value = false;
  const prevRsData01 = rsData01.value;
  rsData01.value = false;
  isLoading.value = true;
  // isShowBanner.value = false
  formData.value.langYN = 'N';

  try {
    await searchEoway();
  } catch (error) {
    // 에러 발생 시 이전 결과 복원
    rsData01.value = prevRsData01;
    throw error;
  }

  resetSkyDay();
  await nextTick();
  callback();

  // proBodyRef 상태를 watchEffect로 감지
  watchEffect(() => {
    if (proBodyRef.value && proBodyRef.value.resetAll) {
      proBodyRef.value.resetAll();
    } else {
      DEV_MODE && console.log('proBodyRef 초기화 대기 중...');
    }
  });

  await nextTick();

  if (Object.keys(sajuData.value).length !== 0) {
    // M_saju_check는 searchEoway() 내부에서 이미 1회 발화하므로 여기서 중복 발화하지 않음
    // (로그인 후 자동 재조회 경로의 사주조회 더블카운트 방지)
    addContentLogAPI({
      contentType: 'M_saju_check_option_opt3',
      option3: formData.value.option1 ? '야자시' : '일반시',
    });
    addContentLogAPI({
      contentType: 'M_saju_check_option_opt3',
      option3: formData.value.option2 === null ? '모름' : formData.value.option2 ? '동지' : '입춘',
    });
    addContentLogAPI({
      contentType: 'M_saju_check_option_opt3',
      option3: formData.value.option4 ? '한국시' : '표준시',
    });
    addContentLogAPI({
      contentType: 'manse_option_pro_gen_opt3',
      option3: '프로',
    });
  }
};

const oldFormData = ref({});
const rsGuestUserId = ref(null);
const searchEoway = async (useInputData = false, showLoading = true) => {
  if (areaTxt.value === null) {
    formData.value.addressTxt = areaTxt.value;
    formData.value.bornAreaId = null;
  }

  if (!useInputData) {
    let formValidate = true;
    if (formData.value.gender === null) {
      showDialogAlert('성별을 확인해 주세요.');
      //alert('성별을 선택해 주세요.');
      formValidate = false;
      return;
    }
    if (
      formData.value.isLunar === null &&
      (formData.value.isLeapYear === null || formData.value.isLeapYear === false)
    ) {
      showDialogAlert('양력, 음력, 윤달을 선택해 주세요.');
      formValidate = false;
      return;
    }
    if (formData.value.year === '' || formData.value.month === '' || formData.value.day === '') {
      showDialogAlert('생년월일을 입력해주세요.');
      formValidate = false;
      return;
    }

    const year = parseInt(formData.value.year, 10);
    if (year < 1900 || year > 2100) {
      showDialogAlert('사주조회는 1900년~2100년까지 가능합니다.');
      formValidate = false;
      return;
    }
    if (
      (formData.value.hour === '' || formData.value.min === '') &&
      formData.value.birth_time !== true
    ) {
      showDialogAlert('출생시간을 입력해주세요.');
      formValidate = false;
      return;
    }

    formValidate = useFun.checkDateTimeValid({
      checkType: 'date',
      inputYear: formData.value.year,
      inputMonth: formData.value.month,
      inputDay: formData.value.day,
    });
    if (
      !formValidate ||
      isNaN(formData.value.year) ||
      isNaN(formData.value.month) ||
      isNaN(formData.value.day)
    ) {
      showDialogAlert('생년월일을 올바르게 입력해주세요.');
      formValidate = false;
      return;
    }
    formValidate = useFun.checkDateTimeValid({
      checkType: 'time',
      inputHour: formData.value.hour,
      inputMinute: formData.value.min,
    });
    if (
      (!formValidate || isNaN(formData.value.hour) || isNaN(formData.value.min)) &&
      formData.value.birth_time !== true
    ) {
      showDialogAlert('출생시간을 올바르게 입력해주세요.');
      return;
    }
    if (!formValidate) return;

    formData.value.langYN = 'N';
  }

  try {
    if (showLoading !== false) {
      isLoading.value = true;
    }
    const { month, day, hour, min } = formData.value;
    formData.value.birth = `${formData.value.year}-${month.toString().padStart(2, '0')}-${day
      .toString()
      .padStart(2, '0')}`;
    formData.value.birthTime = `${hour.toString().padStart(2, '0')}:${min
      .toString()
      .padStart(2, '0')}`;
    // 시간모름 처리: 이전 조회의 unknownTime 상태가 남지 않도록 매 조회마다 초기화
    formData.value.unknownTime = 'N';
    if (formData.value.birth_time) {
      formData.value.hour = '--';
      formData.value.min = '--';
      formData.value.birthTime = '25:00';
      formData.value.unknownTime = 'Y';
    }
    if (formData.value.birthTime === '25:00') formData.value.unknownTime = 'Y';
    if (!formData.value.isLeapYear) formData.value.isLeapYear = false;

    //지역값 없을 시 삭제하고 보내기
    if (formData.value.bornAreaId === null) {
      delete formData.value.bornAreaId;
      delete oldFormData.value.bornAreaId;
    }

    let params = reactive({}); // reactive로 초기화
    if (formData.value.langYN === 'Y') {
      Object.assign(params, oldFormData.value); // oldFormData의 값을 params에 할당
    } else {
      Object.assign(params, formData.value); // formData의 값을 params에 할당
    }

    if (useInputData && sajuDataForm.value) {
      params = sajuDataForm.value;
      params.lang = formData.value.lang;
    }

    // 대운적용을 위한 사주조회 params 저장
    searchParams.value = params;

    const data = await fastSearch(params); // 저장목록 조회

    if (!useInputData) {
      sajuDataForm.value = params;
    }

    isShowBanner.value = false;

    if (data.errmsg?.code === 1) {
      showDialogAlert(data.errmsg.msg);
      return;
    }
    logGuestUserHistory({ ...params, option5: '사주입력' });
    sajuData.value = data;
    // 옵션 변경(한글/한자·프로/일반)으로 인한 재조회(useInputData=true)는
    // 사주조회 행위가 아니므로 M_saju_check 발화 제외 → 활동 KPI 사주조회 오카운트 방지
    if (!useInputData) {
      addContentLogAPI('M_saju_check');
    }
    rsDataAll.value = data;
    transdata.value = data.transdata;
    inputdata.value = data.inputdata;
    birthData.value = data.birthganji;
    solarDate.value = data.solar;
    solarWeekday.value = data.solarWeekday;
    lunarDate.value = data.lunar;
    birthJijanggan.value = data.jijanggan;
    rsData01.value = true;

    // useWoonManagement 컴포저블 초기화 (의존성 주입)
    woonManagement.initialize({
      inputdata,
      birthData,
      langMode,
      formData,
      searchParams,
      five,
    });

    // 대운 데이터 설정 (컴포저블이 처리)
    woonManagement.setInitialData(data);

    // useSajuSave 컴포저블 초기화
    sajuSave.initialize({
      systemId,
      sajuData,
      transdata,
      solarDate,
      lunarDate,
      userGroupId,
      userGroupName,
      memoContent,
      pgmode: ref(null),
      id,
      isPopSajuGroupChange,
      showDialogAlert,
      getParams,
    });

    const currentYear = new Date().getFullYear();
    isFutureSaju.value = inputdata.value.year > currentYear;
    isUnknownTime.value = inputdata.value.unknown_time === 'Y';
    const skyValues = [
      five.getHanjaTxt(birthData.value.sky.year.value, 'sky'),
      five.getHanjaTxt(birthData.value.sky.month.value, 'sky'),
      five.getHanjaTxt(birthData.value.sky.day.value, 'sky'),
      birthData.value.sky.hour?.value !== '-'
        ? five.getHanjaTxt(birthData.value.sky.hour.value, 'sky')
        : '-',
    ];
    const earthValues = [
      five.getHanjaTxt(birthData.value.earth.year.value, 'earth'),
      five.getHanjaTxt(birthData.value.earth.month.value, 'earth'),
      five.getHanjaTxt(birthData.value.earth.day.value, 'earth'),
      birthData.value.earth.hour?.value !== '-'
        ? five.getHanjaTxt(birthData.value.earth.hour.value, 'earth')
        : '-',
    ];
    birthEightTxt.value = skyValues.join('') + earthValues.join('');
    if (birthEightTxt.value === '') {
      const skyValues = [
        birthData.value.sky.year.value,
        birthData.value.sky.month.value,
        birthData.value.sky.day.value,
        birthData.value.sky.hour?.value ?? '-',
      ];
      const earthValues = [
        birthData.value.earth.year.value,
        birthData.value.earth.month.value,
        birthData.value.earth.day.value,
        birthData.value.earth.hour?.value ?? '-',
      ];
      birthEightTxt.value = skyValues.join('') + earthValues.join('');
    }

    hapDataVal.value = five
      .convertHap({ hap: data?.hap, isSky: true, langMode: langMode.value })
      .join('ㆍ');
    hapDataValEarth.value = five
      .convertHap({ hap: data?.hap, isSky: false, langMode: langMode.value })
      .join('ㆍ');

    // 대운 역순 정렬은 컴포저블에서 처리
    if (sortMode.value === 'desc') {
      reverseDaeunData();
    }

    gongDataVal.value = useFun.convertGongmangTaewol(data);

    // 오행구성 수정
    birthFiveCnt.value = five.fiveColorCnt(curDaeun.value, data.birthganji?.five);

    //갤러리닫기
    rsDataNor.value = false;
    //if (langMode.value) rsDataNor.value = true;

    //기존 데이터 저장해 두기
    oldFormData.value = cloneDeep(formData.value);
    //oldFormData.value = formData.value;

    // 사주 조회시 갤러리형 자동으로 펼쳐짐
    rsDataNor.value = true;

    //FIXME: 전문가용 데이터 호출
    await setData();
  } catch (error) {
    console.error('Failed to load users:', error);
  } finally {
    //rsData01.value =false;
    isLoading.value = false;

    // 모바일: 조회 후 옵션선택 박스 아래로 스크롤 (초기화/조회/저장 버튼이 자연스럽게 보이도록)
    if (isMobile.value) {
      nextTick(() => {
        const mobileOption = document.querySelector('.mobile-option');
        if (mobileOption) {
          const headerHeight = 52; // MobileHeader 높이
          const rect = mobileOption.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          window.scrollTo({
            top: scrollTop + rect.bottom - headerHeight,
            behavior: 'smooth',
          });
        }
      });
    }
  }
};
const ManseBirthMainArea = ref(null);
// getWoon, getSeun, getMonth는 useWoonManagement에서 가져옴

// hapBlDataSky, hapBlDataEarth, clickDaeunCheck, updateSelectedWoon, applyWoon은 useWoonManagement에서 가져옴

/* //FIXME:  순서변경 */
const sortChange = () => {
  addContentLogAPI({
    contentType: 'manse_option_sort_opt3',
    option3: sortMode.value === 'desc' ? '년월일시' : '시일월년',
  });
  sortMode.value = sortMode.value === 'desc' ? 'asc' : 'desc';
  birthTitle.value = birthTitle.value.split('').reverse().join('');
  dataOrder.value.reverse();
  reverseDaeunDataWithUpdate();
  setSortModeOption(sortMode.value);
};

/** 드로어에서 특정 정렬 모드로 변경 */
const sortChangeTo = (mode) => {
  if (sortMode.value === mode) return;
  sortChange();
};

// reverseDaeunData는 useWoonManagement에서 가져옴
// reverseData는 internal 유틸리티로 남겨둠
const reverseData = (data) => {
  return Object.values(data).reverse();
};

// rsDataAll 업데이트를 위한 래퍼 함수
const reverseDaeunDataWithUpdate = () => {
  reverseDaeunData();
  rsDataAll.value.daeuns.sky = daeunsData.value.sky;
  rsDataAll.value.daeuns.earth = daeunsData.value.earth;
  rsDataAll.value.wonjinsal = reverseData(rsDataAll.value.wonjinsal);
  rsDataAll.value.guimunsal = reverseData(rsDataAll.value.guimunsal);
  setData();
};

/*글자 및 색상변경*/
const searchEowayTrans = async (mode) => {
  addContentLogAPI({
    contentType: 'manse_option_chn_kor_opt3',
    option3: mode === 'CHNKOR' ? '전체' : mode === 'KOR' ? '한글' : '한자',
  });

  // formData만 먼저 설정 (langMode는 나중에)
  if (mode === 'CHNKOR') {
    formData.value.lang = 'KOR';
    oldFormData.value.lang = 'KOR';
  } else {
    formData.value.lang = mode;
    oldFormData.value.lang = mode;
  }
  formData.value.langYN = 'Y';

  // 서버에서 한자 변환된 데이터 받아오기 (로딩 스피너 없이)
  await searchEoway(true, false); // useInputData=true, showLoading=false

  // 데이터 받아온 후에 langMode 업데이트 (이제 한 번에 한자로 렌더링됨)
  langMode.value = mode;

  // langMode 변경 후 천간/지지 변화 텍스트 재계산 (convertHap은 langMode에 따라 한자/한글 변환)
  if (rsDataAll.value?.hap) {
    hapDataVal.value = five
      .convertHap({ hap: rsDataAll.value.hap, isSky: true, langMode: langMode.value })
      .join('ㆍ');
    hapDataValEarth.value = five
      .convertHap({ hap: rsDataAll.value.hap, isSky: false, langMode: langMode.value })
      .join('ㆍ');
  }

  setLangModeOption(mode);
  if (isDaeunCheck.value) applyWoon({ isLangChanged: true });
};

const searchEowayTransColor = (mode) => {
  addContentLogAPI({
    contentType: 'manse_option_color_opt3',
    option3: mode === 'fntcolor01' ? '바탕색' : mode === 'fntcolor02' ? '글자색' : '흑백',
  });
  colorMode.value = mode;
  setColorSelectOption(mode);
};

const id = ref(null);

watch(
  selectedOptions,
  (newOptions) => {
    if (newOptions) {
      const { isKoreaTime, earlyTime, birthType, langType, fiveElementTheme, wonGukSort } =
        newOptions;

      // 야자시/조자시
      formData.value.option1 = earlyTime;
      // 절기 선택
      formData.value.option2 =
        birthType === 'winter'
          ? true
          : birthType === 'spring'
            ? false
            : birthType === 'unknown'
              ? null
              : true;
      // 한국시 적용 - 값이 다를 때만 업데이트
      if (formData.value.option4 !== isKoreaTime) {
        formData.value.option4 = isKoreaTime;
      }
    }
  },
  { immediate: true },
);
const proBodyRef = ref(null);
const popQuickMenuRef = ref(null);
const memoContent = ref('');
const memoCount = ref(0);
async function getParams() {
  DEV_MODE && console.log('getParams 호출됨');
  try {
    const result = await getDetail(id.value);
    sajuCreatedDate.value = result.createdAt;
    const { integrationBirth } = result;

    if (!result.isMySaju && integrationBirth && integrationBirth.includes('T')) {
      const dateTime = integrationBirth.split('T');
      const [year, month, day] = dateTime[0].split('-');
      const [hour, min] = dateTime[1]?.split(':') || [];

      formData.value.year = year;
      formData.value.month = month.padStart(2, '0');
      formData.value.day = day.padStart(2, '0');
      formData.value.hour = (hour || '').padStart(2, '0');
      formData.value.min = (min || '').padStart(2, '0');
    }

    formData.value.birthTime = result.birthTime;
    rsGuestUserId.value = result.guestUserId;
    formData.value.userName = result.guestUserName;
    // 선택된 그룹정보 저장
    selectedGroup.value = result;
    userGroupId.value = result.userGroup?.userGroupId;
    userGroupName.value = result.userGroup?.name || '등록 사주';

    memoCount.value = result.memoCount;
    formData.value.gender = result.gender === 'MEN' ? 1 : 0;
    formData.value.isLunar = result.isLuna === null ? false : result.isLuna;
    formData.value.isLeapYear = result.isLeapYear;
    formData.value.addressTxt = result.bornArea;
    formData.value.bornAreaId = `${result.bornAreaId}`;
    formData.value.option1 = result.option1;
    formData.value.option2 = result.option2;
    formData.value.option3 = result.option3;
    formData.value.option4 = result.option4;
    formData.value.option5 = result.option5;
    DEV_MODE && console.log('선택된그룹:', userGroupName.value);
  } catch (error) {
    goEorror01();
    console.log(error);
    // 에러 처리
    throw error; // 에러가 발생하면 다시 throw
  }
}

// 스낵바 띄우기
function triggerSnackbar(message) {
  snackbar.value.showSnackbar(message);
}

//저장하기(로그인체크)
async function regSave(callback = () => {}) {
  addContentLogAPI('manse_btn_saju_save');
  checkUserLoginAndOpenDialog().then((isLoggedIn) => {
    if (isLoggedIn) {
      const year = parseInt(formData.value.year, 10);
      if (year < 1900 || year > 2100) {
        showDialogAlert('사주조회는 1900년~2100년까지 가능합니다.');
        return;
      }
      showDialogConfirm('저장하시겠습니까?', '낭만분석에서 확인 가능합니다.', '저장', () => {
        getUserGroups();
        callback();
      });
    }
  });
}

//그룹정보 조회
async function getUserGroups() {
  if (userGroupId.value) {
    formData.value.userGroup = { userGroupId: userGroupId.value };
  } else {
    formData.value.userGroupId = null;
    formData.value.userGroup = null;
  }
  // useSajuSave 컴포저블 사용
  await sajuSave.saveSajuData();
}

/** 조회된 사주 데이터 초기화 */
const resetSajuData = () => {
  sajuData.value = {};
  userGroupName.value = '등록 사주';
  userGroupId.value = null;
  memoContent.value = '';
  memoCount.value = 0;
};

// saveSajuData, convertSajuData, convert 함수들, saveSajuMemo, changeGroup은 useSajuSave에서 가져옴
const changeGroup = sajuSave.changeGroup;

function handleGroupChange(selectedGroupItem) {
  addContentLogAPI('');
  checkUserLoginAndOpenDialog().then((isLoggedIn) => {
    if (isLoggedIn) {
      groupListOpen();
      selectedGroup.value = selectedGroupItem;
      selectedListItem.value = selectedGroupItem;
      DEV_MODE && console.log('selectedGroup', selectedGroup.value);
      DEV_MODE && console.log('selectedListItem', selectedListItem.value);
    }
  });
}

//정보 조회 값 세팅
const searchInput = (item) => {
  Object.assign(formData.value, item);
  const timeParts = item.time.split(':'); // 시간을 ":"를 기준으로 분리
  const hour = timeParts[0]; // 시간 부분을 가져옴
  const minute = timeParts[1]; // 분 부분을 가져옴

  formData.value.hour = hour; // 시간을 formData의 속성에 할당
  formData.value.min = minute; // 분을 formData의 속성에 할당
};

const areaTxt = ref('');

watch(areaTxt, (newArea) => {
  if (newArea && newArea.length > 0) {
    setUserSelectOption('isKoreaTime', true);
    formData.value.option4 = true;
  }
});

//지역선택
const selectAreaFinish = (selectAreaItem) => {
  areaTxt.value = `${selectAreaItem.value.korCountryName}, ${selectAreaItem.value.korCityName} (${selectAreaItem.value.longitudeType} ${selectAreaItem.value.degrees}도 ${selectAreaItem.value.minutes}분)`;
  formData.value.bornAreaId = `${selectAreaItem.value.id}`;
  isPopAreaBox.value = false;
  formData.value.addressTxt = `${selectAreaItem.value.korCountryName}, ${selectAreaItem.value.korCityName} (${selectAreaItem.value.longitudeType} ${selectAreaItem.value.degrees}도 ${selectAreaItem.value.minutes}분)`;
};

// =============== 8. 팝업/모달 ===============
// 전문가용 일간 팝업
const isPopIlgan = ref(false);
const isIlganClosed = ref(false);
const closePopIlgan = () => {
  isPopIlgan.value = false;
  isIlganClosed.value = true;
};

const isOpenPopFiveStar = ref(false);
const openPopFiveStar = (mode) => {
  const args = {
    contentType: 'manse_btn_click_ohangdo_opt3',
    option3: mode,
  };
  addContentLogAPI(args);

  isOpenPopFiveStar.value = true;
};
const closePopFiveStar = () => (isOpenPopFiveStar.value = false);

const isPopMansePrint = ref(false);
const closePopMansePrint = () => {
  isPopMansePrint.value = false;
};

// 좌측 배너 클릭시
const onClickGuideBanner = () => {
  const articleId = DEV_MODE ? 7340 : 9064;
  router.push(`/nangman-center/notice/${articleId}`);
};

const dialogPrint = ref(null);
async function printManse() {
  let cnt = 0;
  cnt = Object.keys(rsDataAll.value)?.length;
  if (cnt > 0) {
    // 출력물 세션 저장
    sessionStorage.setItem('printData', JSON.stringify(rsDataAll.value));
    sessionStorage.setItem('printformData', JSON.stringify(sajuDataForm.value));
    sessionStorage.setItem('sortMode', sortMode.value);
    sessionStorage.setItem('langMode', langMode.value);
    sessionStorage.setItem('toggleProMode', toggleProMode.value);
    isPopMansePrint.value = true;
  } else {
    showDialogAlert('사주조회 후 이용하여 주시기 바랍니다.');
  }
}

const selectedMemoListItem = ref({});
const selectedMemoListItemVal = ref(null);
const memoModeType = ref(null);

//메모 열기
const openMemoRegFirst = () => {
  addContentLogAPI('');
  checkUserLoginAndOpenDialog().then((isLoggedIn) => {
    if (isLoggedIn) {
      const item = {};
      item.userName = formData.value.userName;
      item.birthTime = `${formData.value.hour}:${formData.value.min}`;
      item.gender = useFun.getGenderTxt3(formData.value.gender);
      item.solar = transdata.value.solar;
      item.lunar = transdata.value.lunar;
      item.age = transdata.value.age;
      item.ageKor = transdata.value.kor_age;
      item.firstReg = true;
      item.guestUserMemoContent = memoContent.value;
      item.guestUserId = id;
      selectedMemoListItem.value = item;
      selectedMemoListItemVal.value = item;
      selectedMemoListItemVal.value.content = memoContent.value;
      memoModeType.value = 'reg_first';
      openMemoReg();
    }
  });
};

//메모저장
const saveMemoFirst = (mContent) => {
  memoContent.value = mContent;
  memoCount.value = 1;
};

const memoData = ref([]);
const memoListData = ref({});
const listGroupMemoRefresh = async (memoTxt = '') => {
  try {
    const params = {};
    if (memoTxt !== '') params.content = memoTxt;
    params.guestUserId = route.params.id;
    params.sort = 'createdAt.desc';
    params.size = 30;
    const data = await getMemoList(params); // 그룹리스트 조회
    memoData.value = data.content;

    // listData 초기화
    Object.keys(memoListData.value).forEach((key) => {
      delete memoListData.value[key];
    });

    // 데이터 그룹화
    memoData.value.forEach((data) => {
      const createdAt = new Date(data.createdAt);
      const monthYear = `${createdAt.getFullYear()}.${`0${createdAt.getMonth() + 1}`.slice(-2)}`;

      if (!memoListData.value[monthYear]) {
        memoListData.value[monthYear] = [];
      }

      memoListData.value[monthYear].push(data);
    });
    memoCount.value = memoData.value.length;
  } catch (error) {
    console.error('Failed to load users:', error);
  }
};

const listGroupMemoDel = () => {
  showDialogAlert('삭제되었습니다.', '');
  listGroupMemoRefresh();
};

function openMemoListReg(selectedItem) {
  selectedMemoListItem.value = selectedItem;
  selectedMemoListItemVal.value = null;
  memoModeType.value = 'list';
  openMemoReg();
}

function openMemoListView(selectedMemo, selectedItem) {
  selectedMemoListItemVal.value = selectedItem;
  selectedMemoListItem.value = selectedMemo;
  memoModeType.value = 'edit';
  openMemoReg();
}

const handleReportClick = async (typ) => {
  addContentLogAPI('');
  let cnt = 0;
  cnt = Object.keys(rsDataAll.value)?.length;
  if (cnt > 0 && typ === 'reset') {
    await showDialogConfirm(
      '새로운 사주로 입력 하시겠습니까?',
      '입력한 내용이 초기화 됩니다.',
      '확인',
      () => handleReportClick2(typ),
    );
    return;
  } else {
    handleReportClick2(typ);
  }
};

const handleReportClick2 = (typ) => {
  const isOptionDefault =
    formData.value.option1 === DEFAULT_FORM_OPTIONS.option1 &&
    formData.value.option2 === DEFAULT_FORM_OPTIONS.option2 &&
    formData.value.option3 === DEFAULT_FORM_OPTIONS.option3 &&
    formData.value.option4 === DEFAULT_FORM_OPTIONS.option4;
  const isCalendarOptionDefault =
    formData.value.isLunar === DEFAULT_CALENDAR_OPTIONS.isLunar &&
    (formData.value.isLeapYear === DEFAULT_CALENDAR_OPTIONS.isLeapYear ||
      formData.value.isLeapYear === false);

  if (
    rsData01.value === false &&
    (areaTxt.value === null || areaTxt.value === '') &&
    formData.value.gender === null &&
    formData.value.year === '' &&
    formData.value.month === '' &&
    formData.value.hour === '' &&
    formData.value.day === '' &&
    formData.value.min === '' &&
    isCalendarOptionDefault &&
    isOptionDefault
  ) {
    showDialogAlert('초기화 상태입니다. ');
    return;
  }
  if (typ === 'reset') {
    rsData01.value = false;
    rsDataNor.value = false;
    areaTxt.value = null;
    userGroupName.value = '등록 사주';
    isSaved.value = false;
    isShowBanner.value = true;
    formData.value = createEmptyFormData();
    setUserSelectOption('earlyTime', DEFAULT_FORM_OPTIONS.option1);
    setUserSelectOption('birthType', DEFAULT_FORM_OPTIONS.option2 ? 'winter' : 'spring');
    setUserSelectOption('isKoreaTime', DEFAULT_FORM_OPTIONS.option4);
    //popAreaCity.value = null
    // 출력물 비우기
    rsDataAll.value = {};
  }
  if (typ === 'rs01') {
    rsData01.value = true;
    rsDataNor.value = false;
  }
  if (typ === 'nor') {
    rsDataVip.value = false;
    rsDataNor.value = true;
  }
  if (typ === 'vip') {
    rsDataNor.value = false;
    rsDataVip.value = true;
  }
};
// =============== 9. Alert/Confirm ===============
const dialogAlert = ref(null);
const dialogTitle = ref('');
const dialogTitle2 = ref('');
const dialogText = ref('');
const dialogNextUrl = ref(null);
const warningTxt = ref('');

const dialogConfrim = ref(null);
const doFun = ref(null);

//FIX 알림창
const showDialogAlert = (title, text = '', nextUrl = '') => {
  dialogTitle.value = title;
  dialogText.value = text || '';
  if (nextUrl === '/saju-service') {
    dialogNextUrl.value = async () => {
      localStorage.removeItem('tempFormData');
      resetSajuData();
      formData.value = createEmptyFormData();
      areaTxt.value = '';
      rsData01.value = false;
      isShowBanner.value = true;
      await router.push('/saju-service');
    };
  } else {
    dialogNextUrl.value = nextUrl;
  }
  dialogAlert.value.openAlertDialog();
};
const showDialogConfirm = (title, title2 = '', text = '', onConfirm = () => {}, warning) => {
  dialogTitle.value = title;
  dialogTitle2.value = title2;
  dialogText.value = text;
  dialogNextUrl.value = onConfirm; // 함수로 전달
  warningTxt.value = warning;

  dialogConfrim.value.openConfirmDialog();
};

const isPopNameBox = ref(false);

const openUserName = () => {
  addContentLogAPI('');
  isPopNameBox.value = true;
};

const onChangeName = async (name) => {
  await updateGuestUserName(id.value, name);
  isPopNameBox.value = false;
  inputdata.value.name = name;
  await getParams();
  await searchEoway();
};

/** 언어 선택 업데이트 */
const setLangModeOption = (langMode) => {
  if (!langMode) return;

  const langTypeMap = {
    CHNKOR: 'MIX',
    CHN: 'HANJA',
    KOR: 'KOR',
  };

  const langType = langTypeMap[langMode] || 'MIX';

  setUserSelectOption('langType', langType);
};
/** 컬러 선택 업데이트 */
const setColorSelectOption = (fntcolor) => {
  if (!fntcolor) return;

  const themeMap = {
    fntcolor01: 'BACKGROUND',
    fntcolor02: 'TEXT',
    fntcolor03: 'BLACKWHITE',
  };

  const theme = themeMap[fntcolor] || 'BACKGROUND';

  setUserSelectOption('fiveElementTheme', theme);
};
/** 정렬 선택 업데이트 */
const setSortModeOption = (sortMode) => {
  if (!sortMode) return;

  const sortMap = {
    desc: 'HHDDMMYYYY',
    asc: 'YYYYMMDDHH',
  };

  const wonGukSort = sortMap[sortMode] || 'HHDDMMYYYY';

  setUserSelectOption('wonGukSort', wonGukSort);
};

/** 모바일 드로어에서 옵션 변경 시 처리 (DOM event 없이 직접 key/value로 처리) */
const handleDrawerOptionChanged = (newFormData) => {
  const prevFormData = formData.value;
  formData.value = newFormData;

  // 한국시 변경 검증
  if (newFormData.option4 !== prevFormData.option4) {
    const isAreaSet = !(areaTxt.value === null || areaTxt.value === '');
    if (!newFormData.option4 && isAreaSet) {
      showDialogAlert('지역을 적용하신 경우, 한국시만 적용됩니다.');
      formData.value.option4 = true;
      setUserSelectOption('isKoreaTime', true);
      return;
    }
    setUserSelectOption('isKoreaTime', newFormData.option4);
  }

  // 야자시/조자시 변경
  if (newFormData.option1 !== prevFormData.option1) {
    setUserSelectOption('earlyTime', newFormData.option1);
  }

  // 절기 변경
  if (newFormData.option2 !== prevFormData.option2) {
    if (newFormData.option2 === true) {
      setUserSelectOption('birthType', 'winter');
    } else if (newFormData.option2 === false) {
      setUserSelectOption('birthType', 'spring');
    } else {
      setUserSelectOption('birthType', 'unknown');
    }
  }
};

/** 로그인 확인했을 때 */
const redirectToLogin = ({ redirectOption }) => {
  manseStore.setTempFormData(formData.value);

  const query = {
    to: '/saju-input',
  };

  if (redirectOption) {
    query.redirectOption = redirectOption;
  }

  router.push({
    path: '/auth/login',
    query,
  });
};
/** 로그인 confirm dialog */
// 게스트일 경우 로그인 요청
const checkUserLoginAndOpenDialog = async () => {
  if (sessionStorage.getItem('X-Token')) return true;

  localStorage.setItem('tempFormData', JSON.stringify(formData.value));
  if (isApp.value) {
    showDialogConfirm('로그인 후 이용 가능합니다.', '', '로그인', () => {
      sendMessageToApp({ action: 'login' });
    });
  } else {
    showDialogConfirm('로그인 후 이용 가능합니다.', '', '로그인', () => redirectToLogin({}));
  }
  return false;
};

/** 용어 설명 팝업 열기 */
const openDetail = (index) => {
  currentCardIndex.value = index;
  isPopManseDetail.value = true;
  addContentLogAPI({
    contentType: 'manse_saju_card_popup_opt3',
    option3: `${contentOptionMap[index]}`,
  });
};

const ilganInfo = ref({});

// 모바일 퀵메뉴 탭 상태 (대운, 오행도, 신살)
const mobileActiveTab = ref('daeun');

// 모바일 세운/월운 컨테이너 ref
const mobileSeunContainerRef = ref(null);
const mobileWolunContainerRef = ref(null);

// 모바일 운 컨테이너에서 선택된(active) 항목이 보이도록 스크롤
const scrollWoonToActive = (containerRef) => {
  setTimeout(() => {
    const container = containerRef.value?.$el;
    if (container && container.classList.contains('data-0102-box')) {
      const activeItem = container.querySelector('.birth-item-box.active');
      if (activeItem) {
        const containerRect = container.getBoundingClientRect();
        const activeRect = activeItem.getBoundingClientRect();
        // 선택된 항목이 컨테이너 가운데에 오도록 스크롤
        const scrollOffset =
          activeRect.left - containerRect.left - containerRect.width / 2 + activeRect.width / 2;
        container.scrollLeft += scrollOffset;
      } else {
        // active 항목이 없으면 오른쪽 끝으로
        container.scrollLeft = container.scrollWidth;
      }
    }
  }, 150);
};

// 모바일에서 세운/월운 데이터 로드 시 오른쪽 끝으로 스크롤
watch(
  yearsData,
  (newYearsData) => {
    if (isMobile.value && newYearsData?.sky?.length) {
      scrollWoonToActive(mobileSeunContainerRef);
    }
  },
  { flush: 'post' },
);

watch(
  monthsData,
  (newMonthsData) => {
    if (isMobile.value && newMonthsData?.sky?.length) {
      scrollWoonToActive(mobileWolunContainerRef);
    }
  },
  { flush: 'post' },
);

// 세운/월운 펼침 시 스크롤
watch(isSeWoonOpen, (isOpen) => {
  if (isMobile.value && isOpen) {
    scrollWoonToActive(mobileSeunContainerRef);
  }
});

watch(isWolWoonOpen, (isOpen) => {
  if (isMobile.value && isOpen) {
    scrollWoonToActive(mobileWolunContainerRef);
  }
});

// 모바일 퀵메뉴 핸들러
const handleMobileMenuClick = (menuId) => {
  switch (menuId) {
    case 'ilgan':
      clickIlganButton(toggleProMode.value === 'pro' ? '프로' : '일반');
      break;
    case 'sajupuri':
      // 사주풀이 다이얼로그 열기 (시간모름/미래사주 검증 후)
      validateAnalysisAccess('reading');
      break;
    case 'nangmaniljin':
      // 낭만일진 다이얼로그 열기
      validateAnalysisAccess('iljin');
      break;
    case 'nangmansunghyang':
      // 낭만성향 다이얼로그 열기
      validateAnalysisAccess('disposition');
      break;
    case 'yongshin':
      // 용신분석 다이얼로그 열기
      validateAnalysisAccess('analysis');
      break;
    case 'daeun':
    case 'ohaengdo':
    case 'sinsal':
      // 탭 전환은 v-model로 처리됨
      break;
  }
};

/** 상세분석 데이터 접근 불가 사주 검사 (시간모름, 미래사주) */
const validateAnalysisAccess = (type) => {
  if (isFutureSaju.value) {
    showDialogAlert('내년 이후 사주는\n상세분석 서비스 제공이 어렵습니다.');
    return;
  }
  if (isUnknownTime.value) {
    showDialogAlert('시간 모름인 사주는\n상세분석 서비스 제공이 어렵습니다.');
    return;
  }
  switch (type) {
    case 'reading':
      popQuickMenuRef.value?.navigateToAnalysis('reading');
      break;
    case 'iljin':
      popQuickMenuRef.value?.openSajuIljinDialog();
      break;
    case 'disposition':
      popQuickMenuRef.value?.navigateToAnalysis('disposition');
      break;
    case 'analysis':
      popQuickMenuRef.value?.navigateToAnalysis('analysis');
      break;
  }
};

/** 오행도 내 '일간' 버튼 클릭 */
const openIlgan = (data) => {
  const args = {
    contentType: 'manse_btn_click_ilgan_opt3',
    option3: toggleProMode.value === 'pro' ? '프로' : '일반',
  };
  addContentLogAPI(args);

  isPopIlgan.value = true;
  ilganInfo.value = data;
};

/* rs 모드 일 경우 본인 정보가 아닐 경우 /error/error01 */
const goEorror01 = () => {
  router.push('/error/error01');
};

// 일반 - 오행도
const selectedData = ref(null);

const isActive = ref(false);
const handleSkyDayClick = ({ globalIdx, isSkyDayClicked }) => {
  if (selectedData.value === globalIdx) {
    // 동일한요소 재 클릭시 초기화
    resetSkyDay();
  } else {
    // 새로운 요소 선택시
    if (globalIdx === -1) {
      return;
    } else {
      selectedData.value = globalIdx;
      isActive.value = true;
    }
  }
};
const starRef = ref(null);
const resetSkyDay = () => {
  selectedData.value = null;
  isActive.value = false;
  closePopIlgan();
  watchEffect(() => {
    if (starRef.value && starRef.value.resetSelectedState) {
      starRef.value.resetSelectedState();
    } else {
      DEV_MODE && console.log('starRef 초기화 대기 중...');
    }
  });
};

// 베너 클릭시 공지사항 이동
const goToNotice = () => {
  addContentLogAPI('');
  if (DEV_MODE) {
    window.open('/nangman-center/notice/6731');
  } else {
    window.open('/nangman-center/notice/9075');
  }
};

const fiveToDayMap = {
  목: 'thu',
  화: 'tue',
  토: 'sat',
  금: 'fri',
  수: 'wed',
};
const convertFiveToDay = (five) => {
  return fiveToDayMap[five] || '';
};
/** '일간 특성' 버튼 클릭 - mode: 프로/일반 */
const clickIlganButton = (mode) => {
  const args = {
    contentType: 'manse_btn_click_ilgan_feature_opt3',
    option3: mode,
  };
  addContentLogAPI(args);

  const ilganKor = sajuData.value.birthganji.sky.day.value;
  const ilganChn = five.getHanjaTxt(ilganKor, 'sky');
  const whichDayKor = five.getColorFiveText(ilganKor);
  const whichDay = convertFiveToDay(whichDayKor);
  ilganInfo.value = {
    char: ilganChn,
    whichDay,
  };
  isIlganClosed.value = false;
  isPopIlgan.value = true;
};

// =============== 10. 라이프사이클 ===============
onMounted(async () => {
  // 앱: 로그인 후 복귀 시 localStorage에서 폼 데이터 복원
  if (isApp.value) {
    const tempData = localStorage.getItem('tempFormData');
    if (tempData) {
      try {
        formData.value = JSON.parse(tempData);
        areaTxt.value = formData.value.addressTxt;
        localStorage.removeItem('tempFormData');
        await searchEowayDo();
      } catch (e) {
        localStorage.removeItem('tempFormData');
      }
    }
    return;
  }

  // 웹: 저장하기에서 로그인 후 재접속했을 때
  const userId = await getUserLogin();
  const isLoadData = route.query?.loadTempFormData === 'true';

  if (userId && manseStore.tempFormData && isLoadData) {
    formData.value = manseStore.tempFormData;
    areaTxt.value = manseStore.tempFormData.addressTxt;

    if (route.query?.redirectOption) {
      switch (route.query.redirectOption) {
        case 'ilgan':
          await searchEowayDo(() => clickIlganButton('일반'));
          break;
        case 'reading':
          await searchEowayDo(() => popQuickMenuRef.value?.navigateToAnalysis('reading'));
          break;
        case 'fortune':
          await searchEowayDo(() => popQuickMenuRef.value?.openSajuIljinDialog());
          break;
        case 'yongsin':
          await searchEowayDo(() => popQuickMenuRef.value?.navigateToAnalysis('analysis'));
          break;
        case 'personality':
          await searchEowayDo(() => popQuickMenuRef.value?.navigateToAnalysis('disposition'));
          break;

        default:
          await searchEowayDo();
          break;
      }
    } else {
      await searchEowayDo();
    }

    manseStore.clearTempFormData();
  } else if (!isLoadData) {
    manseStore.clearTempFormData();
  }
});

useBackLayer({
  id: 'saju-input-option-drawer',
  isOpen: isOptionDrawerOpen,
  closeFromBack: () => {
    isOptionDrawerOpen.value = false;
  },
});
useBackLayer({
  id: 'saju-input-group-change',
  isOpen: isPopSajuGroupChange,
  closeFromBack: closeGroupChange,
});
useBackLayer({
  id: 'saju-input-group-add',
  isOpen: isPopSajuGroupAdd,
  closeFromBack: closeGroupAdd,
});
useBackLayer({
  id: 'saju-input-group-setting',
  isOpen: isPopSajuGroupSetting,
  closeFromBack: closeGroupSetting,
});
useBackLayer({
  id: 'saju-input-memo-list',
  isOpen: isPopMemoList,
  closeFromBack: closeMemoList,
});
useBackLayer({
  id: 'saju-input-memo-edit',
  isOpen: isPopMemoReg,
  closeFromBack: closeMemoReg,
});
useBackLayer({
  id: 'saju-input-summer-time',
  isOpen: isPopSummerTime,
  closeFromBack: () => {
    isPopSummerTime.value = false;
  },
});
useBackLayer({
  id: 'saju-input-ganji',
  isOpen: isPopGanjiBox,
  closeFromBack: () => {
    isPopGanjiBox.value = false;
  },
});
useBackLayer({
  id: 'saju-input-area',
  isOpen: isPopAreaBox,
  closeFromBack: () => {
    isPopAreaBox.value = false;
  },
});
useBackLayer({
  id: 'saju-input-print',
  isOpen: isPopMansePrint,
  closeFromBack: closePopMansePrint,
});
useBackLayer({
  id: 'saju-input-card-detail',
  isOpen: isPopManseDetail,
  closeFromBack: closePopManseDetail,
});
useBackLayer({
  id: 'saju-input-ilgan',
  isOpen: isPopIlgan,
  closeFromBack: closePopIlgan,
});
useBackLayer({
  id: 'saju-input-five-star',
  isOpen: isOpenPopFiveStar,
  closeFromBack: closePopFiveStar,
});
useBackLayer({
  id: 'saju-input-share',
  isOpen: isMobileShareOpen,
  closeFromBack: closeMobileShare,
});
useBackLayer({
  id: 'saju-input-korean-time-option',
  isOpen: isOpenPopKSTOption,
  closeFromBack: () => closePopOption('KST'),
});
useBackLayer({
  id: 'saju-input-night-time-option',
  isOpen: isOpenPopJasiOption,
  closeFromBack: () => closePopOption('Jasi'),
});
useBackLayer({
  id: 'saju-input-solar-term-option',
  isOpen: isOpenPopSTOption,
  closeFromBack: () => closePopOption('ST'),
});
useBackLayer({
  id: 'saju-input-qr',
  isOpen: isPopQrBox,
  closeFromBack: closePopQrBox,
});
useBackLayer({
  id: 'saju-input-name-edit',
  isOpen: isPopNameBox,
  closeFromBack: () => {
    isPopNameBox.value = false;
  },
});
</script>

<template>
  <div class="content-main saju-input bg-body-gray" :class="[colorMode, langMode]">
    <MobileHeader
      title="사주입력"
      :show-back="false"
      :show-menu="!isApp"
      :show-settings="true"
      :sort-mode="sortMode"
      :isSearched="rsData01"
      @save="regSave"
      @share="openMobileShare"
      @settings="isOptionDrawerOpen = true"
      @sortChange="sortChange"
    />
    <MobileOptionDrawer
      v-model="isOptionDrawerOpen"
      :langMode="langMode"
      :colorMode="colorMode"
      :sortMode="sortMode"
      :formData="formData"
      @changeLang="searchEowayTrans"
      @changeColor="searchEowayTransColor"
      @changeSort="sortChangeTo"
      @update:formData="handleDrawerOptionChanged"
    />
    <section class="inner-base manse-hd-wrap">
      <!-- 사주입력 왼쪽 배너 -->
      <SajuLeftBanner @qrOpen="onClickGuideBanner" />
      <div class="spacer" />
      <section class="inner-base-3">
        <section class="manse-search-box mb10">
          <!-- 사주입력 헤더 (PC 전용) -->
          <div class="saju-header-pc">
            <SajuHeader />
          </div>
          <VForm ref="contentForm">
            <div class="search-form-box">
              <!-- 사주입력 헤더 (모바일 전용) -->
              <div class="saju-input-mobile-header">
                <div class="saju-input-mobile-header__title">사주 입력</div>
                <button type="button" class="btn-bg-point" @click="openPopGanjiBox">
                  사주간지 입력
                </button>
              </div>
              <!-- 사주입력 내용 입력 -->
              <SajuForm
                :isCheckboxChecked="isCheckboxChecked"
                v-model:formData="formData"
                v-model:areaTxt="areaTxt"
                @openGanjiPopup="openPopGanjiBox"
                @openAreaPopup="handleFocusArea"
                @search="searchEoway"
              />
              <!-- 사주입력 옵션 -->
              <SajuOption
                :formData="formData"
                :isSearched="rsData01"
                v-model:isOptionInput="isOptionInput"
                @openTooltip="clickTooltip"
                @resetReport="handleReportClick"
                @search="searchEoway"
                @save="regSave"
                @openSummerTime="openSummerTime"
                @optionChanged="handleDrawerOptionChanged"
              />

              <!-- // end : manse-search-box // -->
            </div>
          </VForm>
        </section>
      </section>
      <ComLatest ref="refComLatest" :pr-data="rsDataAll" @printclick="printManse()" />
    </section>
    <SajuResultPlaceholder v-if="isShowBanner && !isMobile" />
    <section>
      <LoadingSpinner v-if="isLoading" />
      <transition name="fade" appear>
        <div v-if="rsData01">
          <section class="inner-base result-10-box" style="position: relative">
            <section ref="targetDiv" class="result-01-box mb20">
              <!-- 사주결과 헤더 -->
              <SajuResultHeader
                :inputdata="inputdata"
                :transdata="transdata"
                :birthData="birthData"
                :langMode="langMode"
                :colorMode="colorMode"
                :sortMode="sortMode"
                :toggleProMode="toggleProMode"
                :isSaved="isSaved"
                :memoCount="memoCount"
                :userGroupName="userGroupName"
                :selectedGroup="selectedGroup"
                :solarDate="solarDate"
                :solarWeekday="solarWeekday"
                :lunarDate="lunarDate"
                :useFun="useFun"
                :five="five"
                :showHanja="showHanja"
                :showIlganBadge="true"
                @clickIlganIcon="clickIlganButton(toggleProMode === 'pro' ? '프로' : '일반')"
                @openUserName="openUserName"
                @searchEowayTrans="searchEowayTrans"
                @searchEowayTransColor="searchEowayTransColor"
                @sortChange="sortChange"
                @changeProMode="changeProMode"
                @handleGroupChange="handleGroupChange"
                @openMemoRegFirst="openMemoRegFirst"
                @regSave="regSave"
              />
              <!-- //TODO : 전문가 버전 체크 -->
              <div
                class="data-sec-01-box"
                style="display: flex; justify-content: center"
                v-if="toggleProMode === 'pro'"
              >
                <div class="date-pro-root-box">
                  <SajuResultPro
                    ref="proBodyRef"
                    :birthTitle="birthTitle"
                    :dataOrder="dataOrder"
                    :rsDataAll="rsDataAll"
                    :langMode="langMode"
                    :toggleProMode="toggleProMode"
                    :firstSegment="firstSegment"
                    :secondSegment="secondSegment"
                    :thirdSegment="thirdSegment"
                    :fourthSegment="fourthSegment"
                    :storedLangMode="langMode"
                    :birthEightTxt="birthEightTxt"
                    :birthData="birthData"
                    :hapDataVal="hapDataVal"
                    :hapBlDataSky="hapBlDataSky"
                    :hapDataValEarth="hapDataValEarth"
                    :isLoading="isLoading"
                    @open-ilgan="openIlgan"
                    @close-ilgan="closePopIlgan()"
                    @openPopFiveStar="openPopFiveStar"
                    @closePopFiveStar="closePopFiveStar"
                    @clickIlganButton="clickIlganButton"
                  />
                </div>
              </div>

              <!-- //TODO : 일반 버전 체크 -->
              <div class="data-sec-01-box" v-if="toggleProMode === 'nor'">
                <div class="data-0101-root-box">
                  <SajuMyeongsik
                    ref="ManseBirthMainArea"
                    :pr-birth-data="birthData"
                    :pr-birth-jijanggan="birthJijanggan"
                    :pr-lang-mode="langMode"
                    :pr-birth-title="birthTitle"
                    :pr-transdata="transdata"
                    :pr-type="'birth'"
                    :pr-cur-daeun="curDaeun"
                    :pr-cur-seun="curSeun"
                    :selected-data="selectedData"
                    :is-active="isActive"
                    :sort-mode="sortMode"
                  />
                  <div style="margin-bottom: 16px" />
                  <!-- 오행도: PC에서만 표시, 모바일에서는 퀵메뉴 탭에서 표시 -->
                  <OhaengChart
                    v-if="!isMobile"
                    ref="starRef"
                    :birthData="birthData"
                    :birthEightTxt="birthEightTxt"
                    @skyDay-click="handleSkyDayClick"
                    @skyDay-reset="resetSkyDay"
                    @open-ilgan="openIlgan"
                    @close-ilgan="closePopIlgan()"
                    @openPopFiveStar="openPopFiveStar"
                    @closePopFiveStar="closePopFiveStar"
                    @clickIlganButton="clickIlganButton"
                  />
                  <OhaengSummary
                    :isDaeunCheck="isDaeunCheck"
                    :birthData="birthData"
                    :birthFiveCnt="birthFiveCnt"
                    :isApplyDaeun="isapplyWoon"
                    :hapDataVal="hapDataVal"
                    :isLoading="isLoading"
                    :hapBlDataSky="hapBlDataSky"
                    :hapDataValEarth="hapDataValEarth"
                    :hapBlDataEarth="hapBlDataEarth"
                    :rsDataAll="rsDataAll"
                    :toggleProMode="toggleProMode"
                    :five="five"
                    :langMode="langMode"
                    @clickDaeunCheck="clickDaeunCheck"
                  />
                  <!-- 모바일 퀵메뉴 - 오행요약 바로 아래에 배치 (모바일에서만 표시) -->
                  <MobileQuickMenu
                    v-if="isMobile"
                    :ilganValue="
                      showHanja(birthData?.sky?.day?.value, 'sky') ||
                      birthData?.sky?.day?.value ||
                      ''
                    "
                    :ilganColorClass="five.getColorFive(birthData?.sky?.day?.value)"
                    v-model:activeMenu="mobileActiveTab"
                    @menuClick="handleMobileMenuClick"
                  />

                  <!-- 모바일 탭 컨텐츠 영역 (모바일에서만 표시) -->
                  <div v-if="isMobile" class="mobile-tab-content">
                    <!-- 대운 탭 -->
                    <div v-show="mobileActiveTab === 'daeun'" class="mobile-tab-panel">
                      <div class="mobile-inner-wrap">
                        <div class="deaun-main-box">
                          <div class="tit-box">
                            <span>대운</span>
                            <button
                              class="btn-fold"
                              :class="{ folded: !isMobileAllSubOpen }"
                              @click="toggleMobileDaeun"
                            >
                              펼침/닫기
                            </button>
                          </div>
                          <WoonContainer
                            :pr-birth-data="daeunsData"
                            :pr-lang-mode="langMode"
                            :pr-type="'daeun'"
                            :pr-cur-num="daeunCurNum"
                            @clickDaeun="getWoon"
                            @updateBlylml="updateSelectedWoon"
                          />
                          <!-- 첫 대운 변기일 정보 (모바일) -->
                          <div
                            v-if="bigluckinfo && firstDaeunDateFormatted"
                            class="first-daeun-info mobile"
                          >
                            ＊<span class="first-daeun-info__label">첫 대운 변기일:</span>
                            {{ firstDaeunDateFormatted }} (출생일부터
                            {{ bigluckinfo.elapsed_period }})
                          </div>
                        </div>

                        <!-- 세운 -->
                        <div v-if="isDaeunYear && isMobileSeWoonOpen" class="deaun-main-box">
                          <div class="tit-box">
                            <span>세운</span>
                            <button
                              class="btn-fold"
                              :class="{ folded: !isMobileSeWoonOpen }"
                              @click="toggleMobileSeWoon"
                            >
                              펼침/닫기
                            </button>
                          </div>
                          <WoonContainer
                            ref="mobileSeunContainerRef"
                            :pr-birth-data="yearsData"
                            :pr-lang-mode="langMode"
                            :pr-type="'seun'"
                            :pr-cur-num="daeunYearCurNum"
                            @clickDaeun="getWoon"
                            @updateBlylml="updateSelectedWoon"
                          />
                        </div>

                        <!-- 월운 -->
                        <div v-if="isDaeunMonth && isMobileWolWoonOpen" class="deaun-main-box">
                          <div class="tit-box">
                            <span>월운</span>
                            <button
                              class="btn-fold"
                              :class="{ folded: !isMobileWolWoonOpen }"
                              @click="toggleMobileWolWoon"
                            >
                              펼침/닫기
                            </button>
                          </div>
                          <WoonContainer
                            ref="mobileWolunContainerRef"
                            :pr-birth-data="monthsData"
                            :pr-lang-mode="langMode"
                            :pr-type="'month'"
                            :class="'month'"
                            :pr-cur-num="daeunMonthCurNum"
                            @clickDaeun="getWoon"
                            @updateBlylml="updateSelectedWoon"
                            @applyWoon="applyWoon"
                          />
                        </div>
                      </div>
                    </div>

                    <!-- 오행도 탭 -->
                    <div v-show="mobileActiveTab === 'ohaengdo'" class="mobile-tab-panel mobile-tab-panel--ohaeng">
                      <div class="mobile-inner-wrap">
                        <OhaengChart
                          :birthData="birthData"
                          :birthEightTxt="birthEightTxt"
                          @skyDay-click="handleSkyDayClick"
                          @skyDay-reset="resetSkyDay"
                          @open-ilgan="openIlgan"
                          @close-ilgan="closePopIlgan()"
                          @openPopFiveStar="openPopFiveStar"
                          @closePopFiveStar="closePopFiveStar"
                          @clickIlganButton="clickIlganButton"
                        />
                      </div>
                    </div>

                    <!-- 신살 탭 -->
                    <div
                      v-show="mobileActiveTab === 'sinsal'"
                      class="mobile-tab-panel mobile-tab-panel--sinsal"
                    >
                      <div class="mobile-inner-wrap">
                        <SinsalCardGrid
                          :pr-data-all="rsDataAll"
                          :pr-sort-mode="sortMode"
                          :pr-lang-mode="langMode"
                          :is-mobile="true"
                          @openDetail="openDetail"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Woon_Container 시작 (PC에서만 표시) -->
                <div v-if="!isMobile" class="right-luck-box">
                  <!-- 대운 시작 -->
                  <div class="deaun-main-box">
                    <div class="tit-box">
                      <span>대운</span>
                    </div>
                    <Transition>
                      <WoonContainer
                        :pr-birth-data="daeunsData"
                        :pr-lang-mode="langMode"
                        :pr-type="'daeun'"
                        :pr-cur-num="daeunCurNum"
                        @clickDaeun="getWoon"
                        @updateBlylml="updateSelectedWoon"
                      />
                    </Transition>
                    <!-- 첫 대운 변기일 정보 -->
                    <div v-if="bigluckinfo && firstDaeunDateFormatted" class="first-daeun-info">
                      ＊<span class="first-daeun-info__label">첫 대운 변기일:</span>
                      {{ firstDaeunDateFormatted }} (출생일부터 {{ bigluckinfo.elapsed_period }})
                    </div>
                  </div>
                  <!-- 대운 종료 -->

                  <!-- 세운 시작 -->
                  <div v-if="isDaeunYear" class="deaun-main-box">
                    <div class="tit-box">
                      <span>세운</span>
                      <button
                        class="btn-fold"
                        :class="{ folded: !isSeWoonOpen }"
                        @click="toggleSeWoon"
                      >
                        펼침/닫기
                      </button>
                    </div>
                    <Transition name="slide-fade">
                      <WoonContainer
                        v-show="isSeWoonOpen"
                        :pr-birth-data="yearsData"
                        :pr-lang-mode="langMode"
                        :pr-type="'seun'"
                        :pr-cur-num="daeunYearCurNum"
                        @clickDaeun="getWoon"
                        @updateBlylml="updateSelectedWoon"
                      />
                    </Transition>
                  </div>
                  <!-- 세운 종료 -->

                  <!-- 월운 시작 -->
                  <div v-if="isDaeunMonth" class="deaun-main-box">
                    <div class="tit-box">
                      <span>월운</span>
                      <button
                        class="btn-fold"
                        :class="{ folded: !isWolWoonOpen }"
                        @click="toggleWolWoon"
                      >
                        펼침/닫기
                      </button>
                    </div>
                    <Transition name="slide-fade">
                      <WoonContainer
                        v-show="isWolWoonOpen"
                        :pr-birth-data="monthsData"
                        :pr-lang-mode="langMode"
                        :pr-type="'month'"
                        :class="'month'"
                        :pr-cur-num="daeunMonthCurNum"
                        @clickDaeun="getWoon"
                        @updateBlylml="updateSelectedWoon"
                        @applyWoon="applyWoon"
                      />
                    </Transition>
                  </div>
                  <!-- 월운 종료 -->
                  <!-- Woon Container 종료 -->

                  <div v-if="!isMobile" class="normal-banner" @click="goToNotice()" />
                </div>
              </div>
            </section>
          </section>
        </div>
      </transition>
    </section>
    <!-- 신살 카드 그리드 (PC에서만 표시) -->
    <transition name="fade" appear>
      <section v-if="rsDataNor && toggleProMode === 'nor' && !isMobile">
        <section ref="rsGalleryBox" class="hd-inner-base bg-tab-cont manse-btm-ect-list">
          <div class="tab-content-container">
            <div class="tab-content-box">
              <SinsalCardGrid
                :pr-data-all="rsDataAll"
                :pr-sort-mode="sortMode"
                :pr-lang-mode="langMode"
                @openDetail="openDetail"
              />
            </div>
            <div class="register-date">사주 등록일 {{ useFun.getDateTxt(sajuCreatedDate) }}</div>
          </div>
        </section>
      </section>
    </transition>
  </div>

  <!--그룹선택 팝업-->
  <PopSajuGroupChange
    v-if="isPopSajuGroupChange"
    :pr-selected-group="selectedGroup"
    :isDialogVisibleChange="true"
    @click="closeGroupChange"
    @change="changeGroup"
  />

  <!--그룹관리 팝업-->
  <PopAddGroup
    v-if="isPopSajuGroupAdd"
    :currentTab="currentTab"
    @close="closeGroupAdd"
    @change="changeGroup"
    @refresh="groupListDataRefresh"
    @openSetting="openGroupSetting"
  />

  <!-- FIX: Quick menu bar (모바일에서는 MobileQuickMenu 사용하므로 하단 바 숨김) -->
  <QuickMenu
    ref="popQuickMenuRef"
    v-if="rsData01"
    :hideBar="isMobile"
    :rsGuestUserId="rsGuestUserId"
    :pr-inputdata="inputdata"
    :pr-sajuData="sajuData"
    :pr-birth-data="birthData"
    :lang-mode="langMode"
    :five="five"
    :transdata="transdata"
    :solar="solarDate"
    :lunar="lunarDate"
    :isIlganClosed="isIlganClosed"
    :isUnknownTime="isUnknownTime"
    :isFutureSaju="isFutureSaju"
    @clickIlganButton="clickIlganButton"
    @close="closePopQuickMenu"
    @click:outside="closePopQuickMenu"
    @regSave="regSave"
    @redirectToLogin="redirectToLogin"
  />

  <PopMemo
    v-if="isPopMemoList || isPopMemoReg"
    :name="inputdata.name"
    :guest-user-id="route.params.id"
    :pr-selected-memo="selectedMemoListItem"
    :pr-selected-memo-val="selectedMemoListItemVal"
    :pr-memo-mode-type="memoModeType"
    :pr-memo-list-data="memoListData"
    :pr-transdata="transdata"
    :pr-inputdata="inputdata"
    @getMemoList="listGroupMemoRefresh"
    @openMemoView="openMemoListView"
    @openMemoReg="openMemoListReg"
    @saveMemoFirst="saveMemoFirst"
    @listGroupMemoDel="listGroupMemoDel"
    @listGroupMemo="listGroupMemoRefresh"
  />

  <!-- 서머타임 팝업 -->
  <PopSummerTime
    v-if="isPopSummerTime"
    @close="isPopSummerTime = false"
    @click:outside="isPopSummerTime = false"
  />

  <!-- 사주간지입력 (팝업)-->
  <PopGanji
    v-if="isPopGanjiBox"
    @close="isPopGanjiBox = false"
    @searchinput="searchInput"
    @snackbar="triggerSnackbar('한글 사주간지를 다시 확인해 주세요.')"
  />

  <!-- 주소검색 (팝업)-->
  <PopArea
    v-if="isPopAreaBox"
    @close="isPopAreaBox = false"
    @searchinput="searchInput"
    @selectAreaFinish="selectAreaFinish"
  />

  <!--출력화면-->
  <PopMansePrint
    ref="dialogPrint"
    v-if="isPopMansePrint"
    :toggleProMode="toggleProMode"
    @close="closePopMansePrint()"
    @click:outside="closePopMansePrint()"
  />

  <!-- 사주용어이해하기 -->
  <PopCardDetail
    ref="dialogDetail"
    v-if="isPopManseDetail"
    :currentCardIndex="currentCardIndex"
    @close="closePopManseDetail()"
    @click:outside="closePopManseDetail()"
  />

  <!-- 전문가용 - 일간 팝업 -->
  <PopIlgan
    v-if="isPopIlgan"
    :toggleProMode="toggleProMode"
    :ilganInfo="ilganInfo"
    @close="closePopIlgan"
    @click:outside="closePopIlgan"
    @redirectToLogin="redirectToLogin"
  />

  <PopFiveStar
    v-if="isOpenPopFiveStar"
    @close="closePopFiveStar"
    @click:outside="closePopFiveStar"
  />

  <!-- 모바일 공유하기 팝업 -->
  <PopShareBox
    v-if="isMobileShareOpen"
    :pr-data="rsDataAll"
    @close="closeMobileShare"
    @click:outside="closeMobileShare"
  />

  <PopKSTOption
    v-if="isOpenPopKSTOption"
    @close="closePopOption('KST')"
    @click:outside="closePopOption('KST')"
  />
  <PopJasiOption
    v-if="isOpenPopJasiOption"
    @close="closePopOption('Jasi')"
    @click:outside="closePopOption('Jasi')"
  />
  <PopSolarTermOption
    v-if="isOpenPopSTOption"
    @close="closePopOption('ST')"
    @click:outside="closePopOption('ST')"
  />

  <PopQrBox v-if="isPopQrBox" @closePopQrBox="closePopQrBox()" />

  <!--Alert창-->
  <AlertComponent
    ref="dialogAlert"
    :title="dialogTitle"
    :text="dialogText"
    :nextUrl="dialogNextUrl"
    :doFun="doFun"
  />

  <ConfirmComponent
    ref="dialogConfrim"
    :title="dialogTitle"
    :title2="dialogTitle2"
    :text="dialogText"
    :nextUrl="dialogNextUrl"
    :warning="warningTxt"
  />

  <!--이름 수정-->
  <PopNameComponent
    v-if="isPopNameBox"
    :username="inputdata.name"
    @onChangeName="onChangeName"
    @close="isPopNameBox = false"
  />

  <!--스낵바-->
  <Snackbar ref="snackbar" />
</template>

<style lang="scss">
@import '@/assets/css/style.scss';
@import '@/assets/nangman_style/manse.scss';
@import '@/assets/nangman_style/manse_k.scss';
</style>

<style>
.rspage {
  height: 0;
  width: 0;
  visibility: hidden;
}

.tit-row {
  display: flex;
  flex-direction: row;
  gap: 4px;
}

@media (min-width: 600px) {
  .v-dialog.v-dialog-sm .v-overlay__content,
  .v-dialog.v-dialog-lg .v-overlay__content,
  .v-dialog.v-dialog-xl .v-overlay__content {
    inline-size: auto !important;
  }
}
</style>
<style lang="scss" scoped>
.content-main .inner-base.manse-hd-wrap {
  width: 100%;
  max-width: 1360px;
}

.content-main .manse-hd-wrap .inner-base-3 {
  min-width: 0;
}

.content-main .search-form-box {
  grid-template-columns: repeat(auto-fit, minmax(420px, 1fr));
  height: auto;
}

.content-main .search-form-box .search-box {
  min-width: 0;
  height: auto;
}

.content-main .search-form-box .search-box.right {
  padding-left: clamp(16px, 4vw, 68px);
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  opacity: 0;
  position: absolute;
  background: #fff;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
}

// 첫 대운 변기일 정보 스타일
.first-daeun-info {
  margin-top: 8px;
  text-align: right;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: #949494;
  padding-right: 30px; // 대운 컬럼 오른쪽 끝에 맞춤

  &__label {
    font-weight: 700;
  }

  // 모바일용 왼쪽 정렬
  &.mobile {
    text-align: left;
    padding-right: 16px;
    padding-left: 16px;
    margin-top: 0;
    margin-bottom: 16px;
    font-size: 12px;
    line-height: 18px;
  }
}

// 모바일 saju-input 페이지 전체 스크롤바 숨기기
@media (max-width: 768px) {
  :global(html),
  :global(body),
  :global(.manse-layout),
  :global(.manse-layout *) {
    -ms-overflow-style: none !important;
    scrollbar-width: none !important;
  }

  :global(html::-webkit-scrollbar),
  :global(body::-webkit-scrollbar),
  :global(.manse-layout::-webkit-scrollbar),
  :global(.manse-layout *::-webkit-scrollbar) {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
    background: transparent !important;
  }
}

// 모바일 오행요약 스타일 (사주 컬럼처럼 흰색 배경)
:deep(.data-0101-state) {
  @media (max-width: 768px) {
    background: #fff;
    border-radius: 12px;
    margin: 16px;
  }
}

// 모바일 탭 컨텐츠 영역
.mobile-tab-content {
  background: #fff;

  // 운 컨테이너 스타일 - grid 균등 분배, 좁을 때 스크롤바 없이 가로 스크롤
  :deep(.data-0102-box) {
    display: grid !important;
    grid-template-columns: repeat(10, minmax(47.6px, 1fr)) !important;
    gap: 0 !important;
    padding: 8px 12px 12px 12px !important;
    overflow-x: auto !important;
    -ms-overflow-style: none;
    scrollbar-width: none;
    background: #fff !important;

    &::-webkit-scrollbar {
      display: none;
    }

    &.month {
      grid-template-columns: repeat(12, minmax(39.7px, 1fr)) !important;
    }

    .birth-item-box {
      display: flex !important;
      flex-direction: column !important;
      align-items: center !important;
      width: auto !important;
      padding: 8px 2px !important;
      gap: 1px !important;
      background: transparent !important;
      border-radius: 10px !important;
      cursor: pointer !important;
      position: relative !important;
      opacity: 0.7 !important;
      border: none !important;

      &.active {
        opacity: 1 !important;

        .no {
          color: #eb4c10 !important;
        }
      }

      &::before {
        border-radius: 10px !important;
      }

      // 모바일에서 hover 회색 테두리 제거 (터치 시 active 주황색만 표시)
      &:hover::before {
        border-color: transparent !important;
        box-shadow: none !important;
        opacity: 0 !important;
      }
      &.active:hover::before {
        opacity: 1 !important;
        border: 1px solid #eb4c10 !important;
      }

      li {
        width: 100% !important;
        height: auto !important;
        min-height: 14px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        border-bottom: none !important;
        padding: 0 !important;

        span {
          margin-bottom: 2px !important;
        }
      }

      .no {
        font-size: 14px !important;
        font-weight: 700 !important;
        color: #333 !important;
        line-height: 16px !important;
      }

      .no-year {
        font-size: 11px !important;
        font-weight: 400 !important;
        color: #333 !important;
        line-height: 14px !important;
      }

      // 십성 (식신, 장생, 지살 등)
      .txts {
        font-size: 13px !important;
        font-weight: 400 !important;
        color: #333 !important;
        line-height: 14px !important;
        white-space: nowrap !important;
      }

      // 오행 box - 32x32
      .txt-round {
        width: 32px !important;
        height: 32px !important;
        border-radius: 8px !important;
        display: flex !important;
        flex-direction: column !important;
        align-items: center !important;
        justify-content: center !important;
        aspect-ratio: unset !important;

        .txt-main {
          font-size: 22px !important;
          font-weight: 400 !important;
          line-height: 22px !important;
        }

        // 한자+한글 모드 - 한자 왼쪽 위, 한글 오른쪽 아래
        &.chnkor {
          .chn {
            font-size: 18px !important;
            line-height: 18px !important;
            font-weight: 400 !important;
            opacity: 1 !important;
            position: absolute !important;
            top: 2px !important;
            left: 3px !important;
            transform: none !important;
          }

          .txt-main {
            font-size: 10px !important;
            line-height: 10px !important;
            font-weight: 400 !important;
            position: absolute !important;
            bottom: 2px !important;
            right: 3px !important;
          }
        }
      }

      li:nth-child(4),
      li:nth-child(5) {
        padding: 1px 0 !important;
      }
    }
  }

  .mobile-tab-panel {
    padding: 0;
    overflow-x: auto;
    -ms-overflow-style: none !important;
    scrollbar-width: none !important;

    &::-webkit-scrollbar {
      display: none !important;
      width: 0 !important;
      height: 0 !important;
    }

    // 오행도 탭은 가로 스크롤 차단 + 세로 스크롤만 허용 — iOS 드래그 끌림 방지
    &--ohaeng {
      overflow: hidden;
      touch-action: pan-y;

      :deep(*) {
        touch-action: pan-y;
      }
    }

    // 신살 탭은 overflow 해제 (카드 필터 시 내부 스크롤 방지)
    &--sinsal {
      overflow: visible;
      padding-bottom: 24px;

      // 신살 서브탭 - 500px에서 한 줄에 모두 보이도록 축소
      :deep(.sinsal-sub-tabs) {
        gap: 4px;
        padding: 12px 12px;

        .sinsal-sub-tabs__item {
          padding: 5px 10px;
          font-size: 12px;
        }
      }
    }

    // 오행도 모바일 스타일 - 별 안에 한자 짤림 방지
    :deep(.pro-star-box) {
      height: 420px !important;
      overflow: visible !important;
      border: none !important;

      .div-txt {
        font-size: 16px !important;
      }

      .txt-five-box span {
        width: 24px !important;
        height: 24px !important;
        font-size: 16px !important;
      }

      .txt-five-box .txt-line-box {
        width: 70px !important;
      }

      .txt-five-box.st-05 .txt-line-box {
        width: 140px !important;
      }

      // 일간·일지 뱃지 크기 축소
      .txt-five-box .skyDay-label,
      .txt-five-box .skyDay-label-left,
      .txt-five-box .skyDay-label-right {
        font-size: 9px !important;
        height: 14px !important;
        padding: 1px 3px !important;
        line-height: 10px !important;
        border-radius: 7px !important;
      }
    }

    // 대운 탭 내부 스타일
    .deaun-main-box {
      padding: 12px 0 0 0 !important;

      &:last-child {
        margin-bottom: 0;
      }

      .tit-box {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 4px;
        padding: 0 16px !important; // 양쪽 패딩

        span {
          font-size: 16px;
          font-weight: 700;
          color: #333;
        }

        .btn-fold {
          font-size: 12px;
          color: #888;
          text-indent: -9999px;
          width: 24px;
          height: 24px;
          background: url('@/assets/images_manse/icon_up.svg') no-repeat center;
          background-size: 16px;
          transition: transform 0.2s;
          border: 1px solid #e0e0e0;
          border-radius: 50%;

          &.folded {
            transform: rotate(180deg);
          }
        }
      }
    }
  }
}
</style>
